/**
 * Helpers for managing the preloading, especially:
 *  - detecting if the threshold for preloading content when media plays has been reached
 *  - actually preloading the content
 * @module videoPlayer/helpers/preloader
 */
import { getLinkedVariantId } from '../helpers';

const SELECTOR_PRELOAD_PLAYER = '.js-preload-player';
// Events
const EVENT_WILL_PRELOAD = 'player:will-preload';

// Number of seconds before
const SECONDS_BEFORE_CHOICES = 15;
const PERCENT_VIDEO_PLAYED = 25;

/**
 * Sets up the triggering of an event for preloading
 * the content of the next island. This'll happen the first time
 * either of these situations occur on a given video:
 *
 * - a few seconds (SECONDS_BEFORE_CHOICE) before the player shows the choices
 * - after playing a certain percentage (PERCENT_VIDEO_PLAYED) of the video
 *
 * @param {Object} player The player that should trigger the preload cue
 */
export function setupPreloadCue(player) {
  player.on('timeupdate', (event) => {
    // We don't want to unnecessarily compute whether to pre-load
    // after sending the cue so player is marked as pre-loaded once
    // the loading happens
    if (!player.preloaded) {
      event.stopPropagation();

      const preloadTimeFromEnd =
        parseInt(player.variant.prompt.display_at, 10) + SECONDS_BEFORE_CHOICES;

      const shouldPreloadDueToChoices =
        player.currentTime() > player.duration() - preloadTimeFromEnd;

      const shouldPreloadDueToProgress =
        player.currentTime() / player.duration() > PERCENT_VIDEO_PLAYED / 100;

      if (shouldPreloadDueToChoices || shouldPreloadDueToProgress) {
        const willPreloadEvent = new CustomEvent(EVENT_WILL_PRELOAD, {
          bubbles: true,
        });
        // Dispatches will load event and cleans up events to prevent it to set sources further.
        player.el().dispatchEvent(willPreloadEvent);
        player.preloaded = true;
      }
    }
  });
}

/**
 * Updates sources of preload (hidden) video elements.
 * Preloading currently works by adding sources to player elements directly on the page where preload is required.
 * @param {Object} player A videoJS player, typically the main player on the page.
 * @param {Object} variants An object with all variants available.
 * @param {Object} currentVariant Required to retrieve current variant id.
 * @param {Array} history Player history of variants watched. Required to determine which variants to preload.
 */
export function setupPreloading(player, variants, history) {
  // Mark player has not having pre-loaded anymore
  player.preloaded = false;

  // Wait for an event if the player plays a video
  // otherwise preload straight away
  if (player.isVideo) {
    player.one(EVENT_WILL_PRELOAD, () => {
      preload();
    });
  } else {
    preload();
  }

  function preload() {
    const linkedVariants = getLinkedVariants(player.variant, {
      history,
      variants,
    });

    // No need to preload the same variant multiple times
    // TODO: Add a way to no preload the same URLs twice
    // With the feature to re-use videos from the same project
    // this might happen more often than just when playing
    // a placeholder project
    Array.from(new Set(linkedVariants)).forEach((variant, index) => {
      if (variant.is_placeholder && variant.image) {
        preloadImage(variant);
      } else {
        preloadVideo(variant, index);
      }
    });
  }
}

function preloadImage(variant) {
  //https://web.dev/preload-responsive-images/
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = variant.image.url;
  if (variant.image.srcset) {
    link.imageSrcset = Object.entries(variant.image.srcset)
      .map(([width, url]) => `${url} ${width}w`)
      .join(', ');
  }

  // Remove the link so that new sizes of the image are not
  // downloaded when users resize their devices and the image
  // is no longer in preload
  link.onload = () => {
    document.head.removeChild(link);
  };
  link.onerror = () => {
    document.head.removeChild(link);
  };
  document.head.appendChild(link);
}

function preloadVideo(variant, index) {
  const preloadPlayers = document.querySelectorAll(SELECTOR_PRELOAD_PLAYER);
  const preloadPlayer = preloadPlayers[index].player;
  // External links won't have any media URL
  if (variant.media_url) {
    // Reserve setScene to the actual player it does more than setting the source
    preloadPlayer.src({
      src: variant.media_url,
      type: 'application/x-mpegURL',
    });
  }
}

function getLinkedVariants(variant, { history, variants }) {
  return variant.choices
    .map(function (choice) {
      // TODO preload more than one if using advanced logic
      // for now disabling; as calling #getLink will eval behaviour
      // blocks
      if (choice.conditional_logic_block_present) {
        return;
      }

      const linkedVariantId = getLinkedVariantId({ choice, history });
      return variants[linkedVariantId];
    })
    .filter(Boolean);
}
